/**
 * PROJECTS PAGE
 *
 * Full-page project detail view.
 * Shows the last visited project for the current workspace,
 * falling back to the first project if none was visited yet.
 */

import { useEffect } from "react";
import { useUIStore } from "@/stores/uiStore";
import { useWorldStore } from "@/stores/worldStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { ProjectDetailView, getLastVisitedProject } from "@/features/factory/world/ProjectDetailView";
import { Loader2 } from "lucide-react";
import { useLocation, useRoute, Redirect } from "wouter";

export default function ProjectsPage() {
  const { theme } = useUIStore();
  const { projects, isLoading, fetchWorldData } = useWorldStore();
  const { activeWorkspaceId, ensureWorkspaceReady } = useWorkspaceStore();
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/projects/:id");
  const projectIdFromUrl = params?.id;

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    (async () => {
      await ensureWorkspaceReady();
      await fetchWorldData();
    })().catch((err) => console.error("[ProjectsPage] workspace init failed:", err));
  }, [ensureWorkspaceReady, fetchWorldData]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" role="status" aria-label="Loading projects">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  // Determine which project to show: URL param > last visited > first project
  const targetProject = projectIdFromUrl
    ? projects.find((p) => String(p.id) === projectIdFromUrl) ?? projects[0]
    : (() => {
        const lastProjectId = activeWorkspaceId ? getLastVisitedProject(activeWorkspaceId) : null;
        return lastProjectId
          ? projects.find((p) => p.id === lastProjectId) ?? projects[0]
          : projects[0];
      })();

  // Route guard: redirect marketing/content projects to dedicated views
  if (targetProject?.project_category === 'marketing' && targetProject?.project_subtype === 'content') {
    return <Redirect to={`/projects/${targetProject.id}/content`} />;
  }

  if (!targetProject) {
    return (
      <div className="w-full h-full flex items-center justify-center" data-testid="container-projects-page">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground text-sm">No projects yet</p>
          <p className="text-muted-foreground/60 text-xs">Create a project to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" data-testid="container-projects-page">
      <ProjectDetailView
        projectId={targetProject.id}
        onClose={() => setLocation("/")}
        className="flex-1"
      />
    </div>
  );
}
